import React, { useEffect, useRef, useState } from 'react';
import { Field, getIn, useFormikContext } from 'formik';
import { Row, Col, Form } from 'react-bootstrap';
import { FormikInput, Icon } from '../../../components';
import '../survey.scss';

export const ImageChoiceBox = ({ valuePath, position }) => {
  const { setFieldValue, values } = useFormikContext();
  const currentChoice = getIn(values, valuePath) || {};
  const initialPreview = typeof currentChoice.imageUrl === 'string'
    ? currentChoice.imageUrl
    : (currentChoice.imageUrl?.fileUrl || currentChoice.image_url?.file_url || null);
  const [preview, setPreview] = useState(initialPreview);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (initialPreview && preview !== initialPreview) {
      setPreview(initialPreview);
    }
  }, [initialPreview, preview]);

  const handleFile = file => {
    if (!file || !file.type.startsWith('image/')) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    setFieldValue(`${valuePath}[image]`, file);
    setFieldValue(`${valuePath}[imageUrl]`, url);
  };

  const handleFileChange = e => handleFile(e.target.files[0]);

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleClearImage = e => {
    e.stopPropagation();
    setPreview(null);
    setFieldValue(`${valuePath}[image]`, null);
    setFieldValue(`${valuePath}[imageUrl]`, null);
    if (inputRef.current) inputRef.current.value = '';
  };

  useEffect(() => {
    setFieldValue(`${valuePath}[position]`, position);
  }, [position, valuePath, setFieldValue]);

  return (
    <div className="image-choice-container">
      <div
        role="button"
        tabIndex={0}
        aria-label={`Subir imagen ${position}`}
        onClick={() => !preview && inputRef.current?.click()}
        onKeyDown={e => e.key === 'Enter' && !preview && inputRef.current?.click()}
        onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`image-choice-dropzone ${isDragging ? 'dragging' : ''} ${preview ? 'has-preview' : ''}`}
      >
        {preview ? (
          <>
            <img src={preview} alt={`opción ${position}`} className="image-choice-preview-img" />
            <button
              type="button"
              onClick={handleClearImage}
              className="image-choice-clear-btn"
              aria-label="Eliminar imagen"
            />
          </>
        ) : (
          <>
            <Icon icon="image-outline" height="28px" width="28px" />
            <span className="image-choice-upload-text">Subir Imagen</span>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.gif,.webp"
        className="image-choice-input"
        onChange={handleFileChange}
      />
    </div>
  );
};

export const SelectionImagesLayout = ({ valuePath, questionChoicesAttributes = [] }) => {
  const { errors, touched, setFieldValue } = useFormikContext();
  const imageCount = questionChoicesAttributes.filter(c => !c._destroy).length;

  const handleCountChange = e => {
    const newCount = Math.max(2, parseInt(e.target.value, 10) || 2);
    const current = questionChoicesAttributes.filter(c => !c._destroy).length;

    if (newCount > current) {
      const toAdd = newCount - current;
      const extras = Array.from({ length: toAdd }, (_, i) => ({
        label: '',
        position: current + i + 1,
        image: null
      }));
      setFieldValue(`${valuePath}[questionChoicesAttributes]`, [...questionChoicesAttributes, ...extras]);
    } else if (newCount < current) {
      const newChoices = questionChoicesAttributes.slice(0, newCount);
      setFieldValue(`${valuePath}[questionChoicesAttributes]`, newChoices);
    }
  };

  return (
    <div className="mt-3">
      <Row>
        <Col md={10}>
          <Field name={`${valuePath}[label]`}>
            {({ field }) => (
              <FormikInput
                {...field}
                abbr
                label="Pregunta"
                maxLength={100}
                tooltipText="Máximo 100 caracteres"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
      </Row>

      <Row className="align-items-end">
        <Col md={6}>
          <Field name={`${valuePath}[description]`}>
            {({ field }) => (
              <FormikInput
                {...field}
                label="Descripción opcional"
                maxLength={300}
                placeholder="Seleccione una respuesta en imágenes"
                tooltipText="Máximo 300 caracteres"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col xs="auto" className="d-flex align-items-center mb-3 selection-images-count-wrapper">
          <Form.Control
            type="number"
            min={2}
            max={20}
            value={imageCount}
            onChange={handleCountChange}
            className="selection-images-count-input"
          />
          <span className="selection-images-count-label">Imágenes</span>
        </Col>
      </Row>

      <div className="selection-images-grid">
        {questionChoicesAttributes
          .filter(c => !c._destroy)
          .map((choice, visibleIndex) => {
            const realIndex = questionChoicesAttributes.findIndex(
              (c, idx) =>
                !c._destroy && questionChoicesAttributes.slice(0, idx).filter(x => !x._destroy).length === visibleIndex
            );
            return (
              <ImageChoiceBox
                key={choice.id ? `img-choice-${choice.id}` : `img-choice-pos-${choice.position}`}
                valuePath={`${valuePath}[questionChoicesAttributes][${realIndex !== -1 ? realIndex : visibleIndex}]`}
                position={visibleIndex + 1}
              />
            );
          })}
      </div>
    </div>
  );
};

export default SelectionImagesLayout;
